import { Controller, type Control } from 'react-hook-form';
import { t } from '@app/i18n';
import { dataHour } from '@app/format';
import { Button, Field, FieldGrid, SearchCombobox, TextInput } from '@zarya/ui';
import {
  postureHelp,
  postureLabel,
  statusLabel,
  STATUSES,
  type SiteItem,
  type SitePosture,
  type SiteFormValues,
  type SiteStatus,
} from './SiteModel';
import s from './Sites.module.css';

export interface SiteFormProps {
  control: Control<SiteFormValues>;
  /** `true` = edição: o `slug` é somente leitura. */
  editing: boolean;
  /** Ausente = site novo. Só a EDIÇÃO tem carimbo de conversa para mostrar. */
  site?: SiteItem;
  /** O vocabulário de postura **do servidor** (`SiteList.postures`), na ordem L1→L3. */
  postures: SitePosture[];
  /** Texto cinza do campo de template — ver `templatePlaceholder`. */
  templateHint: string;
  onSubmit: () => void;
  /** `id` do `<form>` — o botão do rodapé do drawer submete por `form=`. */
  formId: string;
}

/**
 * Campos do site (P1).
 *
 * O `slug` é somente leitura na edição, e não é conservadorismo: ele é a chave do
 * site dentro do SPIFFE ID de todo workload de um site com template `{site}`.
 * Renomeá-lo moveria o ID de identidades já emitidas — a mesma razão pela qual o
 * slug da identidade congela na 1ª aprovação (FR-048).
 */
export function SiteForm({
  control,
  editing,
  site,
  postures,
  templateHint,
  onSubmit,
  formId,
}: SiteFormProps) {
  return (
    <form id={formId} onSubmit={onSubmit} noValidate>
      <FieldGrid>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <Field
              label={t('sites.form.field.name')}
              span={12}
              required
              error={fieldState.error?.message}
            >
              {(props) => <TextInput {...props} {...field} autoComplete="off" />}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="slug"
          render={({ field, fieldState }) => (
            <Field
              label={t('sites.form.field.slug')}
              span={12}
              required
              error={fieldState.error?.message}
              help={t(
                editing ? 'sites.form.field.slug.help.edit' : 'sites.form.field.slug.help.new',
              )}
              tip={t('sites.form.field.slug.tip')}
            >
              {(props) => (
                <TextInput
                  {...props}
                  {...field}
                  mono
                  autoComplete="off"
                  readOnly={editing}
                  placeholder={t('sites.form.field.slug.placeholder')}
                />
              )}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="executorEndpoint"
          render={({ field, fieldState }) => (
            <Field
              label={t('sites.form.field.endpoint')}
              span={12}
              error={fieldState.error?.message}
              help={t('sites.form.field.endpoint.help')}
              tip={t('sites.form.field.endpoint.tip')}
            >
              {(props) => (
                <TextInput
                  {...props}
                  {...field}
                  mono
                  autoComplete="off"
                  placeholder={t('sites.form.field.endpoint.placeholder')}
                />
              )}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="trustDomain"
          render={({ field, fieldState }) => (
            <Field
              label={t('sites.form.field.trustDomain')}
              span={12}
              error={fieldState.error?.message}
              help={trustDomainHelp(site)}
              tip={t('sites.form.field.trustDomain.tip')}
            >
              {(props) => (
                <>
                  <TextInput
                    {...props}
                    {...field}
                    mono
                    autoComplete="off"
                    placeholder={t('sites.form.field.trustDomain.placeholder')}
                  />
                  {/* aim-p4qh (decisão 1): o valor lido é a sugestão, e um clique o põe no campo. */}
                  {site?.trust_domain_read && field.value.trim() !== site.trust_domain_read ? (
                    <Button
                      variant="soft"
                      size="sm"
                      onClick={() => field.onChange(site.trust_domain_read ?? '')}
                    >
                      {t('sites.form.field.trustDomain.useRead')}
                    </Button>
                  ) : null}
                </>
              )}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="status"
          render={({ field, fieldState }) => (
            <Field
              label={t('sites.form.field.status')}
              span={12}
              required
              error={fieldState.error?.message}
              help={t('sites.form.field.status.help')}
            >
              {({ className: _unused, ...props }) => (
                <SearchCombobox
                  {...props}
                  label={t('sites.form.field.status')}
                  options={STATUSES.map((status) => ({
                    value: status,
                    label: statusLabel(status),
                  }))}
                  value={field.value}
                  onChange={(value) => field.onChange((value ?? 'ativo') as SiteStatus)}
                  placeholder={t('sites.form.field.status.placeholder')}
                />
              )}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="spiffeTemplate"
          render={({ field, fieldState }) => (
            <Field
              label={t('sites.form.field.template')}
              span={12}
              error={fieldState.error?.message}
              help={t('sites.form.field.template.help')}
              tip={t('sites.form.field.template.tip')}
            >
              {(props) => (
                <TextInput
                  {...props}
                  {...field}
                  mono
                  autoComplete="off"
                  // O placeholder é o template que VAI VALER se o campo ficar
                  // vazio. O campo nasce vazio quando ninguém escolheu — ver
                  // `siteDefaults`: preenchê-lo com o efetivo faria o operador
                  // salvar o padrão como escolha explícita sem querer, e a partir
                  // dali este site pararia de acompanhar o padrão.
                  placeholder={templateHint}
                />
              )}
            </Field>
          )}
        />
        <p className={s.templateNote}>{t('sites.form.field.template.notice')}</p>
      </FieldGrid>

      <SitePostureSection control={control} postures={postures} />
      <SiteConnectionSection control={control} site={site} />
    </form>
  );
}

/**
 * A seção «Postura» (S-c) — quanto o produto EXIGE do vínculo entre a identidade e
 * o artefato que a veste.
 *
 * # POR QUE ELA MUDOU DE TELA
 *
 * Era o card «Postura do site» de T21, e o próprio card dizia por quê: ele morava
 * lá porque «há um site», e mudaria para cá quando o drawer da P1 nascesse. Nasceu.
 * A postura é do SITE — com dois clusters, um pode exigir o vínculo e o outro não,
 * e um card de tenant não tem como dizer isso.
 *
 * # O QUE VEIO JUNTO, E QUE É O QUE IMPORTA
 *
 * A confirmação ao DESCER de nível (em `SitesPage`, no caminho do Salvar) e a
 * trilha `site.posture_change` (no `PUT`, ademais do `site.update`). Mudar a
 * postura de lugar é fácil; o que se perde em silêncio são as duas garantias que
 * vinham com ela.
 */
function SitePostureSection({
  control,
  postures,
}: {
  control: Control<SiteFormValues>;
  postures: SitePosture[];
}) {
  return (
    <section className={s.section} aria-label={t('sites.drawer.section.posture')}>
      <h3>{t('sites.drawer.section.posture')}</h3>

      <FieldGrid>
        <Controller
          control={control}
          name="posture"
          render={({ field, fieldState }) => (
            <Field
              label={t('sitePosture.fieldLabel')}
              span={12}
              required
              error={fieldState.error?.message}
              tip={t('sitePosture.fieldTip')}
              help={postureHelp(field.value as SitePosture)}
            >
              {/*
                O `className` do `Field` é para `<input>`; o combobox tem o seu. E o
                `label` vai explícito: `<label htmlFor>` não nomeia um `<button>`,
                que é o que o gatilho do combobox é.
              */}
              {({ className: _unused, ...props }) => (
                <SearchCombobox
                  {...props}
                  label={t('sitePosture.fieldLabel')}
                  // A ordem e o conteúdo da lista vêm do SERVIDOR: o vocabulário é
                  // regra de domínio, e transcrevê-lo aqui criaria um segundo lugar
                  // para a mesma lista — os dois discordariam no dia de um quarto
                  // nível, sem nada ficar vermelho.
                  options={postures.map((posture) => ({
                    value: posture,
                    label: postureLabel(posture),
                    hint: postureHelp(posture),
                  }))}
                  value={field.value}
                  onChange={(value) => field.onChange(value ?? 'artifact_binding')}
                  placeholder={t('sitePosture.searchPlaceholder')}
                />
              )}
            </Field>
          )}
        />
      </FieldGrid>
    </section>
  );
}

/**
 * A seção «Conexão» (S-a) — a credencial da borda DESTE cluster, e o que a última
 * conversa com ele deixou.
 *
 * # POR QUE ELA MUDOU DE TELA
 *
 * Estes campos eram do card «Middleware» de T21 e valiam para o tenant inteiro. Com
 * o endereço no site e a credencial no tenant, era possível **assinar a chamada de
 * um cluster com a credencial de outro** — e ninguém veria: a borda responde 403 sem
 * dizer de quem era a credencial, e o operador lê «executor fora». Aqui, endereço e
 * credencial ficam no mesmo formulário porque ficam na mesma linha.
 *
 * # OS DOIS PARES DE CARIMBO, QUE NÃO SÃO O MESMO
 *
 * «Testar conexão» responde *quando eu testei*; esta seção mostra *quando o produto
 * falou com ele pela última vez*. Um site que ninguém testou hoje não está sem
 * contato, e um site testado à mão pode ter toda ordem real falhando — são perguntas
 * diferentes e por isso duas leituras diferentes.
 */
function SiteConnectionSection({
  control,
  site,
}: {
  control: Control<SiteFormValues>;
  site?: SiteItem;
}) {
  const secretDefined = site?.cf_secret_set ?? false;

  return (
    <section className={s.section} aria-label={t('sites.drawer.section.connection')}>
      <h3>{t('sites.drawer.section.connection')}</h3>

      <FieldGrid>
        <Controller
          control={control}
          name="cfClientId"
          render={({ field, fieldState }) => (
            <Field
              label={t('sites.connection.field.clientId')}
              span={6}
              error={fieldState.error?.message}
              tip={t('sites.connection.field.clientId.tip')}
            >
              {(props) => <TextInput {...props} {...field} mono autoComplete="off" />}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="cfSecret"
          render={({ field, fieldState }) => (
            <Field
              label={t('sites.connection.field.secret')}
              span={6}
              error={fieldState.error?.message}
              // «definido · em branco mantém o valor» × «não definido». É a mesma
              // frase que o card do tenant usava (AC-11), movida byte a byte, e
              // pela mesma razão: o valor nunca volta, então em branco é a única
              // leitura possível de «não mexi».
              help={t(
                secretDefined
                  ? 'sites.connection.field.secret.help.set'
                  : 'sites.connection.field.secret.help.unset',
              )}
              tip={t('sites.connection.field.secret.tip')}
            >
              {(props) => (
                <TextInput
                  {...props}
                  {...field}
                  type="password"
                  autoComplete="new-password"
                  placeholder={secretDefined ? '••••••••' : ''}
                />
              )}
            </Field>
          )}
        />
      </FieldGrid>

      {site ? (
        <>
          <SiteMiddlewareTrace site={site} />
          <p className={s.connectionNote}>{t('sites.connection.notice')}</p>
        </>
      ) : null}
    </section>
  );
}

/**
 * O que a última conversa com este executor deixou — leitura, nunca formulário.
 *
 * FRASES, e não rótulos de lista: é a forma que o card «Middleware» de T21 usava
 * («última sincronização 26/08 12:00 · middleware v0.4.1»), e os textos vieram de
 * lá byte a byte. Manter a voz é o que faz o operador reconhecer, na tela nova, a
 * informação que ele já lia na antiga.
 *
 * Fora do `FieldGrid` de propósito: carimbo é resultado de observação, e desenhá-lo
 * como campo desabilitado convidaria a tentar editá-lo.
 */
/**
 * A ajuda do campo do trust domain (aim-p4qh): o valor que o executor do site reportou na última leitura, e quando;
 * ou, num site que nunca foi lido, o que acontece até o primeiro teste. Num site novo não há leitura nenhuma.
 */
function trustDomainHelp(site?: SiteItem): string {
  const when = dataHour(site?.trust_domain_read_at);
  if (site?.trust_domain_read && when) {
    return t('sites.form.field.trustDomain.read', { value: site.trust_domain_read, when });
  }
  return t('sites.form.field.trustDomain.notRead');
}

function SiteMiddlewareTrace({ site }: { site: SiteItem }) {
  const sync = dataHour(site.middleware_last_sync_at);
  const failure = dataHour(site.middleware_last_failure_at);
  const probe = dataHour(site.last_probe?.at);

  return (
    <dl className={s.trace}>
      <div>
        {/* Nulo é «nunca sincronizado», que é diferente de «sincronizou e depois
            falhou» — a distinção que o par de colunas nulável existe para guardar. */}
        <dd>
          {sync
            ? t('sites.connection.lastSync', { when: sync })
            : t('sites.connection.lastSync.never')}
          {site.middleware_version_seen
            ? ` · ${t('sites.connection.version', { version: site.middleware_version_seen })}`
            : null}
        </dd>
      </div>
      {site.middleware_last_failure_code ? (
        <div>
          <dd>
            {t('sites.connection.lastFailure', {
              code: site.middleware_last_failure_code,
              when: failure ?? '',
            })}
          </dd>
        </div>
      ) : null}
      <div>
        {/* O OUTRO par de carimbo, ao lado do primeiro de propósito: é a única forma
            de o operador ver que são duas perguntas. Nulo aqui é «nunca testado» —
            o mesmo texto da coluna «saúde» da lista, para a tela não ter dois nomes
            para o mesmo estado. */}
        <dd>
          {probe ? t('sites.connection.lastProbe', { when: probe }) : t('sites.list.health.never')}
        </dd>
      </div>
    </dl>
  );
}
